import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, parseISO, differenceInMonths, startOfMonth, addMonths } from 'date-fns';
import { parseDate } from '@/lib/localizedDateUtils';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import type { Brand } from '@/components/finance/BrandSelector';
export type CohortMode = 'received' | 'scheduled';

export interface CohortDataPoint {
  cohortMonth: string;       // "2025-01" - mês de fechamento do deal
  paymentMonth: string;      // "2025-03" - mês do pagamento
  relativeMonth: number;     // 0 = M0, 1 = M1, etc.
  totalReceived: number;
  dealCount: number;
  paymentCount: number;
}

export interface CohortSummary {
  cohortMonth: string;
  cohortLabel: string;       // "Jan/25"
  totalDeals: number;
  totalContractValue: number;
  totalReceived: number;
  monthlyData: Record<number, number>; // relativeMonth -> amount
  percentReceived: number;
}

export interface UseCohortAnalysisOptions {
  businessUnit?: BusinessUnit;
  brand?: Brand;
  /** Modo: 'received' usa paid_at/paid_amount, 'scheduled' usa due_date/amount para parcelas futuras */
  mode?: CohortMode;
  /** Mês/ano inicial do cohort no formato "YYYY-MM" */
  cohortStartMonth?: string;
  /** Mês/ano final do cohort no formato "YYYY-MM" */
  cohortEndMonth?: string;
  /** Número máximo de meses relativos a mostrar (default 24) */
  maxRelativeMonths?: number;
}

// Helper para gerar período default (últimos 18 meses até mês atual)
function getDefaultPeriod(): { start: string; end: string } {
  const now = new Date();
  const end = format(startOfMonth(now), 'yyyy-MM');
  const start = format(addMonths(startOfMonth(now), -17), 'yyyy-MM');
  return { start, end };
}

export function useCohortAnalysis(options: UseCohortAnalysisOptions = {}) {
  const defaultPeriod = getDefaultPeriod();
  
  const { 
    businessUnit = 'all', 
    brand = 'all',
    mode = 'received',
    cohortStartMonth = defaultPeriod.start,
    cohortEndMonth = defaultPeriod.end,
    maxRelativeMonths = 24,
  } = options;

  return useQuery({
    queryKey: ['cohort-analysis', businessUnit, brand, mode, cohortStartMonth, cohortEndMonth, maxRelativeMonths],
    queryFn: async () => {
      // Calcular datas de filtro baseado no período selecionado (usar parseDate local-safe)
      const startDate = `${cohortStartMonth}-01`;
      const endMonthDate = parseDate(`${cohortEndMonth}-01`);
      const endDate = endMonthDate ? format(addMonths(endMonthDate, 1), 'yyyy-MM-dd') : `${cohortEndMonth}-01`;
      // Fetch deals with closed_date and their payments
      let dealsQuery = supabase
        .from('deals')
        .select(`
          id,
          closed_date,
          total_amount,
          business_unit,
          brand,
          payments!payments_deal_id_fkey (
            id,
            paid_at,
            paid_amount,
            due_date,
            amount,
            status
          )
        `)
        .not('closed_date', 'is', null)
        .gte('closed_date', startDate)
        .lt('closed_date', endDate);

      if (businessUnit !== 'all') {
        dealsQuery = dealsQuery.eq('business_unit', businessUnit);
      }
      if (brand !== 'all') {
        dealsQuery = dealsQuery.eq('brand', brand);
      }

      const { data: deals, error } = await dealsQuery;

      if (error) throw error;

      // Process data into cohort format
      const cohortMap = new Map<string, CohortSummary>();
      let maxRelativeMonth = 0;

      deals?.forEach(deal => {
        if (!deal.closed_date) return;

        // Usar parseDate para datas YYYY-MM-DD (evita problemas de timezone)
        const closedDate = parseDate(deal.closed_date);
        if (!closedDate) return;
        
        const cohortMonth = format(closedDate, 'yyyy-MM');
        const cohortLabel = format(closedDate, 'MMM/yy');
        // Initialize cohort if not exists
        if (!cohortMap.has(cohortMonth)) {
          cohortMap.set(cohortMonth, {
            cohortMonth,
            cohortLabel,
            totalDeals: 0,
            totalContractValue: 0,
            totalReceived: 0,
            monthlyData: {},
            percentReceived: 0,
          });
        }

        const cohort = cohortMap.get(cohortMonth)!;
        cohort.totalDeals += 1;
        cohort.totalContractValue += deal.total_amount || 0;

        // Process payments based on mode
        const payments = deal.payments || [];
        
        payments.forEach((payment: { 
          paid_at: string | null; 
          paid_amount: number | null; 
          due_date: string | null;
          amount: number | null;
          status: string;
        }) => {
          if (mode === 'received') {
            // Modo recebido: usa paid_at e paid_amount, só status 'paid'
            if (!payment.paid_at || payment.status !== 'paid') return;

            const paidDate = parseISO(payment.paid_at);
            const relativeMonth = differenceInMonths(paidDate, closedDate);

            // Only count payments from M0 onwards
            if (relativeMonth < 0) return;

            maxRelativeMonth = Math.max(maxRelativeMonth, relativeMonth);

            const amount = payment.paid_amount || 0;
            cohort.totalReceived += amount;
            cohort.monthlyData[relativeMonth] = (cohort.monthlyData[relativeMonth] || 0) + amount;
          } else {
            // Modo previsto/scheduled: usa due_date e valor em aberto
            // Inclui: pending, overdue, partially_paid
            if (!payment.due_date) return;
            if (!['pending', 'overdue', 'partially_paid'].includes(payment.status)) return;

            // Usar parseDate para datas YYYY-MM-DD (evita problemas de timezone)
            const dueDate = parseDate(payment.due_date);
            if (!dueDate) return;
            
            const relativeMonth = differenceInMonths(dueDate, closedDate);
            // Only count future/pending payments from M0 onwards
            if (relativeMonth < 0) return;

            maxRelativeMonth = Math.max(maxRelativeMonth, relativeMonth);

            // Valor em aberto: amount - paid_amount (para partially_paid)
            const totalAmount = payment.amount || 0;
            const paidAmount = payment.paid_amount || 0;
            const openAmount = Math.max(0, totalAmount - paidAmount);

            cohort.totalReceived += openAmount;
            cohort.monthlyData[relativeMonth] = (cohort.monthlyData[relativeMonth] || 0) + openAmount;
          }
        });
      });

      // Calculate percentages
      cohortMap.forEach(cohort => {
        if (cohort.totalContractValue > 0) {
          cohort.percentReceived = (cohort.totalReceived / cohort.totalContractValue) * 100;
        }
      });

      // Sort by cohort month
      const cohorts = Array.from(cohortMap.values()).sort((a, b) => 
        a.cohortMonth.localeCompare(b.cohortMonth)
      );

      // Limitar ao máximo configurado
      const effectiveMaxMonth = Math.min(maxRelativeMonth, maxRelativeMonths);

      // Calculate retention curve (average % received by relative month)
      const retentionCurve: { month: number; label: string; avgPercent: number; cumulativePercent: number }[] = [];
      
      for (let m = 0; m <= effectiveMaxMonth; m++) {
        let totalPercent = 0;
        let cohortCount = 0;

        cohorts.forEach(cohort => {
          if (cohort.totalContractValue > 0 && cohort.monthlyData[m] !== undefined) {
            if (mode === 'received') {
              // Para recebido, só incluir cohorts velhos o suficiente
              const cohortStartDate = parseDate(cohort.cohortMonth + '-01');
              const monthAge = cohortStartDate ? differenceInMonths(new Date(), cohortStartDate) : 0;
              if (monthAge >= m) {
                totalPercent += (cohort.monthlyData[m] / cohort.totalContractValue) * 100;
                cohortCount++;
              }
            } else {
              // Para previsto, incluir todos os cohorts que têm dados
              totalPercent += (cohort.monthlyData[m] / cohort.totalContractValue) * 100;
              cohortCount++;
            }
          }
        });

        const avgPercent = cohortCount > 0 ? totalPercent / cohortCount : 0;
        const prevCumulative = m > 0 && retentionCurve[m - 1] ? retentionCurve[m - 1].cumulativePercent : 0;

        retentionCurve.push({
          month: m,
          label: `M${m}`,
          avgPercent,
          cumulativePercent: prevCumulative + avgPercent,
        });
      }

      // Summary stats
      const totalReceived = cohorts.reduce((sum, c) => sum + c.totalReceived, 0);
      const totalContractValue = cohorts.reduce((sum, c) => sum + c.totalContractValue, 0);
      const totalDeals = cohorts.reduce((sum, c) => sum + c.totalDeals, 0);
      const avgPercentReceived = totalContractValue > 0 
        ? (totalReceived / totalContractValue) * 100 
        : 0;

      return {
        cohorts,
        retentionCurve,
        maxRelativeMonth: effectiveMaxMonth,
        summary: {
          totalReceived,
          totalContractValue,
          totalDeals,
          avgPercentReceived,
        },
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
